/**
 * Number formatting utilities for consistent display across the application
 */

/**
 * Formats a float number to a specified number of decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (2 or 3, defaults to 2)
 * @returns Formatted number as string
 *
 * @example
 * formatDecimal(3.14159) // "3.14"
 * formatDecimal(3.14159, 3) // "3.142"
 * formatDecimal(null) // "0.00"
 * formatDecimal(undefined) // "0.00"
 */
export function formatDecimal(
  value: number | null | undefined,
  decimals: 2 | 3 = 2
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return decimals === 2 ? "0.00" : "0.000";
  }

  return value.toFixed(decimals);
}

/**
 * Formats a percentage value with specified decimal places
 * @param value - The percentage value (e.g., 0.1234 for 12.34%)
 * @param decimals - Number of decimal places (2 or 3, defaults to 2)
 * @param includePercent - Whether to include the % symbol (defaults to true)
 * @returns Formatted percentage as string
 *
 * @example
 * formatPercentage(0.1234) // "12.34%"
 * formatPercentage(0.1234, 3) // "12.340%"
 * formatPercentage(0.1234, 2, false) // "12.34"
 */
export function formatPercentage(
  value: number | null | undefined,
  decimals: 2 | 3 = 2,
  includePercent: boolean = true
): string {
  if (value === null || value === undefined || isNaN(value)) {
    const fallback = decimals === 2 ? "0.00" : "0.000";
    return includePercent ? `${fallback}%` : fallback;
  }

  const percentValue = value * 100;
  const formatted = percentValue.toFixed(decimals);
  return includePercent ? `${formatted}%` : formatted;
}

/**
 * Formats a currency value with specified decimal places
 * @param value - The currency value
 * @param decimals - Number of decimal places (2 or 3, defaults to 2)
 * @param currency - Currency symbol (defaults to '$')
 * @returns Formatted currency as string
 *
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(1234.567, 3) // "$1,234.567"
 * formatCurrency(1234.56, 2, '€') // "€1,234.56"
 */
export function formatCurrency(
  value: number | null | undefined,
  decimals: 2 | 3 = 2,
  currency: string = "$"
): string {
  if (value === null || value === undefined || isNaN(value)) {
    const fallback = decimals === 2 ? "0.00" : "0.000";
    return `${currency}${fallback}`;
  }

  return `${currency}${value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Formats a number with thousands separators and specified decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (2 or 3, defaults to 2)
 * @returns Formatted number as string with thousands separators
 *
 * @example
 * formatNumber(1234.56) // "1,234.56"
 * formatNumber(1234.567, 3) // "1,234.567"
 */
export function formatNumber(
  value: number | null | undefined,
  decimals: 2 | 3 = 2
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return decimals === 2 ? "0.00" : "0.000";
  }

  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats a ratio or multiplier value (like Sharpe ratio, price-to-earnings, etc.)
 * @param value - The ratio value
 * @param decimals - Number of decimal places (2 or 3, defaults to 2)
 * @returns Formatted ratio as string
 *
 * @example
 * formatRatio(1.2345) // "1.23"
 * formatRatio(1.2345, 3) // "1.235"
 */
export function formatRatio(
  value: number | null | undefined,
  decimals: 2 | 3 = 2
): string {
  return formatDecimal(value, decimals);
}

/**
 * Formats a basis points value to percentage with specified decimal places
 * @param basisPoints - The basis points value (e.g., 150 for 1.5%)
 * @param decimals - Number of decimal places (2 or 3, defaults to 2)
 * @returns Formatted percentage as string
 *
 * @example
 * formatBasisPoints(150) // "1.50%"
 * formatBasisPoints(150, 3) // "1.500%"
 */
export function formatBasisPoints(
  basisPoints: number | null | undefined,
  decimals: 2 | 3 = 2
): string {
  if (basisPoints === null || basisPoints === undefined || isNaN(basisPoints)) {
    const fallback = decimals === 2 ? "0.00" : "0.000";
    return `${fallback}%`;
  }

  const percentage = basisPoints / 100;
  return `${percentage.toFixed(decimals)}%`;
}
